// Post-login data fetches via the BFF adapter. Auth-specific logic (login URL,
// token exchange, identity decoding) lives entirely inside the AuthAdapter —
// see src/adapters/auth/.
//
// Strategy is config-driven via productLine.hydration (not product-line id):
//   hydration.type === 'bootstrap' → GET {path}[?surface=…] then map to shell types
//   default / 'experience'         → GET /experience/permissions | personalization

import type { PermissionsManifest, PersonalizationContext } from '@growthaxl/web-foundation';
import {
  getActiveProductLine,
  getAuthAdapter,
  type HydrationConfig,
} from '../../../adapters/AdapterRegistry';
import { bffRequest } from '../../../common/services/httpClient';
import { EXPERIENCE_API_PATHS, TC_API_PATHS } from '../../../constants/apiPaths';

interface BootstrapAppConfig {
  locale?: string;
  date_format?: string;
  currency?: string;
  timezone?: string;
  theme?: string;
}

interface BootstrapResponse {
  tenant_ukey?: string;
  surface?: string;
  navigation?: unknown;
  feature_flags?: unknown;
  app_config?: BootstrapAppConfig;
  legal_index?: unknown;
}

type BootstrapHydration = Extract<HydrationConfig, { type: 'bootstrap' }>;

/** In-flight bootstrap promise — dedupes parallel hydrateSession callers. */
let bootstrapInflight: Promise<BootstrapResponse> | null = null;
let bootstrapInflightKey: string | null = null;

function resolveHydration(): HydrationConfig {
  return getActiveProductLine()?.hydration ?? { type: 'experience' };
}

function bootstrapRequestPath(cfg: BootstrapHydration): string {
  const path = (cfg.path ?? TC_API_PATHS.BOOTSTRAP).replace(/\/$/, '');
  if (cfg.surface?.trim()) {
    return `${path}?surface=${encodeURIComponent(cfg.surface)}`;
  }
  return path;
}

async function fetchBootstrap(cfg: BootstrapHydration): Promise<BootstrapResponse> {
  const key = bootstrapRequestPath(cfg);
  if (!bootstrapInflight || bootstrapInflightKey !== key) {
    bootstrapInflightKey = key;
    bootstrapInflight = bffRequest<BootstrapResponse>(key).finally(() => {
      bootstrapInflight = null;
      bootstrapInflightKey = null;
    });
  }
  return bootstrapInflight;
}

function mapBootstrapToPermissions(
  _bootstrap: BootstrapResponse,
  cfg: BootstrapHydration
): PermissionsManifest {
  const identity = getAuthAdapter().getIdentity();
  if (!identity) {
    throw new Error('No identity available for bootstrap hydration');
  }

  const grantWorkspaces = cfg.grantWorkspaces?.length ? cfg.grantWorkspaces : ['operational'];
  const workspaces: PermissionsManifest['workspaces'] = {};
  for (const id of grantWorkspaces) {
    workspaces[id] = { access: true, actions: [] };
  }

  return {
    userId: identity.user.id,
    tenantId: identity.tenant.id,
    roles: identity.user.roles ?? [],
    workspaces,
    solutions: {},
    agentPermissions: {},
  };
}

function coerceFeatureFlags(raw: Record<string, unknown>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value !== null && typeof value === 'object') continue;
    out[key] = Boolean(value);
  }
  return out;
}

/** Supports flat flags or gateway nested `{ flags, chat_enabled }`. */
function resolveFeatureFlags(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const obj = raw as Record<string, unknown>;
  const nested = obj.flags;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const merged: Record<string, unknown> = { ...(nested as Record<string, unknown>) };
    if (obj.chat_enabled !== undefined) merged.chat_enabled = obj.chat_enabled;
    return coerceFeatureFlags(merged);
  }
  return coerceFeatureFlags(obj);
}

/** Supports array nav or gateway `{ nodes: [] }`. */
function resolveNavigation(nav: unknown): unknown[] {
  if (Array.isArray(nav)) return nav;
  if (nav && typeof nav === 'object' && Array.isArray((nav as { nodes?: unknown }).nodes)) {
    return (nav as { nodes: unknown[] }).nodes;
  }
  return [];
}

function resolveNavigationWithFallback(nav: unknown, cfg: BootstrapHydration): unknown[] {
  const fromApi = resolveNavigation(nav);
  if (fromApi.length > 0) return fromApi;
  if (Array.isArray(cfg.mockNavigation) && cfg.mockNavigation.length > 0) {
    return cfg.mockNavigation;
  }
  return [];
}

function mapBootstrapToPersonalization(
  bootstrap: BootstrapResponse,
  cfg: BootstrapHydration
): PersonalizationContext {
  const app = bootstrap.app_config ?? {};
  const theme = app.theme === 'dark' || app.theme === 'light' ? app.theme : 'light';

  return {
    locale: app.locale ?? 'en',
    dateFormat: app.date_format ?? 'yyyy-MM-dd',
    currency: app.currency ?? 'USD',
    timezone: app.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    theme,
    recentEntities: [],
    preferredViews: {
      navigation: resolveNavigationWithFallback(bootstrap.navigation, cfg),
    },
    featureFlags: resolveFeatureFlags(bootstrap.feature_flags),
  };
}

/** §9.2 — permissions manifest (experience or bootstrap hydration). */
export async function fetchPermissionsManifest(): Promise<PermissionsManifest> {
  const hydration = resolveHydration();
  if (hydration.type === 'bootstrap') {
    const bootstrap = await fetchBootstrap(hydration);
    return mapBootstrapToPermissions(bootstrap, hydration);
  }
  return bffRequest<PermissionsManifest>(EXPERIENCE_API_PATHS.PERMISSIONS);
}

/** §10 — personalization (experience or bootstrap hydration). */
export async function fetchPersonalization(): Promise<PersonalizationContext> {
  const hydration = resolveHydration();
  if (hydration.type === 'bootstrap') {
    const bootstrap = await fetchBootstrap(hydration);
    return mapBootstrapToPersonalization(bootstrap, hydration);
  }
  return bffRequest<PersonalizationContext>(EXPERIENCE_API_PATHS.PERSONALIZATION);
}
